import { useEffect, useRef, useState } from 'react';

import { getEventCounts } from '../services/spatial/spatialEventsClient';
import {
  countableEventIds,
  countsChanged,
  eventCountIntervalMs,
  sanitizeCounts,
  shouldPollEventCounts,
} from '../components/spatial/eventCountPolling';

/**
 * src/hooks/useEventCounts.ts
 *
 * Fill the count seam that was built and never fed.
 *
 * Glue, deliberately. The decisions — which events are worth asking
 * about, how often, whether an answer changed anything — are in
 * `eventCountPolling`, where they can be tested without a renderer.
 * What is left here is the part that needs an effect: a timer, a
 * cancellation, and a version number the map can watch.
 *
 * The counts themselves are not held here. They go to `setEventCounts`
 * and live in exactly one place, which is the map layer's module map
 * that `deriveEventPresentation` reads. Keeping a copy in React state
 * would be the second count system this phase was told not to build,
 * and the two would eventually disagree about the same square.
 *
 * What comes back instead is a version that increments when a number
 * actually changed. The layer re-normalizes on that rather than on
 * every poll, because restamping identical figures onto a whole
 * feature collection is work you can see on a map.
 */
export function useEventCounts(input: {
  enabled: boolean;
  /** The features currently on screen. Only live and soon are asked about. */
  features: readonly { properties?: Record<string, unknown> | null }[] | null | undefined;
  /** Hands the counts to whatever holds them. Called with a fresh object. */
  apply: (counts: Record<string, number>) => void;
  intervalMs?: number;
}): number {
  const { enabled, features, apply, intervalMs } = input;
  const [version, setVersion] = useState(0);

  // Held in refs so a changing callback or a new feature array does not
  // restart the timer. A poll that restarts on every render is a poll
  // that never fires at its stated interval.
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const lastCountsRef = useRef<Record<string, number> | null>(null);

  const ids = countableEventIds(features);
  // The dependency is the id set, not the array: the same events in a
  // different order is the same question.
  const idKey = ids.slice().sort().join(',');

  useEffect(() => {
    const eventIds = idKey ? idKey.split(',') : [];

    // Nothing happening on screen asks nothing. This is what stops the
    // polling when the surface goes quiet, without a second switch.
    if (!shouldPollEventCounts({ enabled, eventIds })) {
      if (lastCountsRef.current) {
        // Clear once, so an event that scrolled off screen does not
        // stay painted with the crowd it had when it left.
        lastCountsRef.current = null;
        applyRef.current({});
        setVersion((v) => v + 1);
      }
      return undefined;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      // Checked at poll time rather than at setup: a tab that was
      // visible when the timer started may not be now.
      if (typeof document !== 'undefined' && document.hidden) return;

      const answer = await getEventCounts(eventIds);
      if (cancelled || !answer.ok) return;

      const counts = sanitizeCounts(
        (answer as { counts?: Record<string, unknown> }).counts,
      );
      if (!countsChanged(lastCountsRef.current, counts)) return;

      lastCountsRef.current = counts;
      applyRef.current(counts);
      setVersion((v) => v + 1);
    };

    void poll();
    const timer = setInterval(() => void poll(), eventCountIntervalMs(intervalMs));
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [enabled, idKey, intervalMs]);

  return version;
}

export default useEventCounts;
